import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { environment as env } from '../../environments/environment';

// Read-only client for the /rest/instrument-details surface. Powers the
// "Instrument Details" panel under /admin/common-data. Supports two
// backend stacks:
//   v2 → /rest/instrument-details      (f2-admin-service2 on web-backend-srvr-1)
//   v1 → /rest-v1/instrument-details   (lseg-service on ws, via Vercel edge rewrite)
// Both endpoints return the same envelope: `{ok, count, rows}` for
// listAll, raw row for getOne. Consumer flip is a URL prefix.
export type InstrumentStack = 'v1' | 'v2';

export interface InstrumentDetail {
  ticker?: string;
  short_name?: string;
  company_common_name?: string;
  short_exchange_name?: string;
  primary_exchange?: string;
  exchange_country_code?: string;
  // NB: `exchange_counttry` typo is intentional — consumer contract.
  exchange_counttry?: string;
  country_of_headquarters?: string;
  sector?: string;
  trbc_economic_sector_name?: string;
  gics_sector?: string;
  gics_industry_group?: string;
  gics_industry?: string;
  sic_code?: number | string;
  sic_description?: string;
  sector_etf?: string;
  optionable?: boolean;
  asset_type?: string;
  city?: string;
  state?: string;
  cik?: string;
  composite_figi?: string;
  share_class_figi?: string;
  active?: boolean;
  short_interest?: number;
  days_to_cover?: number;
  short_interest_avg_daily_volume?: number;
  short_interest_date?: string;
  _source?: string;
  _last_pumped_at?: string;
  [k: string]: any;
}

@Injectable({ providedIn: 'root' })
export class InstrumentDetailsService {
  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private toast: ToastService,
  ) {}

  private _opts() { return { withCredentials: true }; }
  private _base(stack: InstrumentStack = 'v2') {
    // v2 hits the existing /rest/instrument-details rewrite (→ f2-admin-service2).
    // v1 hits /rest-v1/instrument-details (→ lseg-service on ws, via a
    // separate Vercel edge rewrite in vercel.json).
    const prefix = stack === 'v1' ? '/rest-v1' : '/rest';
    return `${env.apiUrl}${prefix}/instrument-details`;
  }

  async listAll(stack: InstrumentStack = 'v2'): Promise<InstrumentDetail[]> {
    const id = this.loadingService.add(`Loading instrument details (${stack})`);
    const url = this._base(stack);
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.rows) ? res.rows as InstrumentDetail[] : [];
    } catch (e: any) {
      this._toastErr(`Instrument details (${stack})`, e, 'F2A_INSTRUMENT_DETAILS_LIST_FAIL', url);
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  private _toastErr(title: string, e: any, code: string, requestUrl?: string) {
    const msg = e?.error?.errors?.[0]?.message || e?.message || 'Unknown error';
    this.toast.error(title, msg, {
      error_code: code,
      customer: 'f2-admin',
      scanner_path: '/admin/common-data',
      request_url: requestUrl || e?.url || undefined,
      http_status: e?.status ?? 0,
      content_type: (e?.headers?.get?.('content-type') as string | undefined) || (e?.error && typeof e.error === 'string' ? 'text/html' : 'application/json'),
      f2_response_body: (typeof e?.error === 'string' ? e.error : JSON.stringify(e?.error || {})).slice(0, 500),
      f2_response_message: msg,
    });
  }
}
